import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useState,
} from "react";

import { Box, Grid, Typography } from "@mui/material";
import "../../components-styles.scss";
import "../../Dossiers/Dossier/dossier-styles.scss";
import {
    OverViewWidgetItem,
    Session,
    DossierStatusEnum,
    ClientDashBoardNavBarMenuItemId,
    DossierDetailType,
} from "../../../types";
import { LayoutBarContext } from "../../../contexts";

import ReactSvgIcon from "../../UIElements/ReactSvgIcon";
import { ReactComponent as DossierAttente } from "../../../public/icons/DossierAttente.svg";
import { ReactComponent as DossierForward } from "../../../public/icons/DossierForward.svg";
import { ReactComponent as DossierEdit } from "../../../public/icons/DossierEdit.svg";
import { ReactComponent as DossierCheck } from "../../../public/icons/DossierCheck.svg";
import { ReactComponent as DossierIcon } from "../../../public/icons/activeIcons/ActiveDossierIcon.svg";
import OverViewWidgets from "../../OverViewWigets/OverViewWidgets";
import HomeHeader from "../../Common/HomeHeader/HomeHeader";
import { useDossier } from "../../Dossiers/useDossier";
import OverViewDossierWrapper from "../../Dossiers/Dossier/OverViews/OverViewDossierWrapper";
import { sessionStorageKey } from "../../../consts";
import { useLocalStorage } from "../../../hooks";
import { getDossierStatus } from "../../../services/dossier.service";
import Dossiers from "../../Dossiers/Dossiers";
import ExportCsvBtn from "../../UIElements/ExportCsvBtn";
import { cloneObject, exportCsv } from "../../../helpers";
import { getDossierStatusByCode } from "../../Dossiers/utils";
import { sortAscending, sortDescending } from "../../utils";
import CustomSwapVert from "../../UIElements/CustomSwapVert";
import { getDossiers } from "../../../services/dossier.service";
import { getRegionById } from "../../../services/establishement.service";

export default function AdminRegionHome() {
    const { updateContent, setActive } = useContext(LayoutBarContext);

    const { storedValue: session } =
        useLocalStorage<Session>(sessionStorageKey);
    const {
        auth: { user: currentUser, currentUserRoleId: userRoleId },
    } = session;
    const counterStruct: any = {
        all: 0,
        completeness: 0,
        instruction: 0,
        other: 0,
    };

    const [dossierStatuses, setDossierStatuses] = useState([]);

    const establishmentGroup = currentUser.establishmentGroup;
    const region = currentUser.region;
    function reducer(state: any, action: any) {
        return state;
    }
    const [regionName, setRegionName] = useState("");
    const fetchRegionName = async () => {
        try {
            if (currentUser.region) {
                const res = await getRegionById(currentUser.region);
                if (res && !res.detail) {
                    setRegionName(res.name); // Set the region name from the API response
                } else {
                    setRegionName("Unknown Region"); // Fallback value
                }
            }
        } catch (error) {
            console.error("Error fetching region name:", error);
            setRegionName("Error fetching region"); // Handle error case
        }
    };
    useEffect(() => {
        fetchRegionName();
    }, []);
    const [dossiersCount, setDossiersCount] = useReducer(
        reducer,
        counterStruct
    );
    const [isDescendingSort, setisDescendingSort] = useState<boolean>(true);

    const {
        toBulkAssignedDossier,
        instructorList,
        currentDossiers,
        _fetchCurrentDossiers,
        downloadCertificate,
        regionList,
        fetchRegionList,
    } = useDossier();

    const setUpDossierStatuses = useCallback(async () => {
        const statuses = await getDossierStatus();
        if (!statuses.detail) {
            setDossierStatuses(statuses);
        }
    }, []);

    useEffect(() => {
        setUpDossierStatuses();
    }, [setUpDossierStatuses]);

    const dossierCounter = useCallback(async () => {
        if (dossierStatuses.length > 0) {
            if (!currentUser.id) {
                return;
            }
            const allDossiers = await getDossiers(currentUser.id, {
                userRoleId,
                establishmentGroup,
                region,
            });
            Object.values(DossierStatusEnum).forEach((code) => {
                const idStatus = (
                    getDossierStatusByCode(code, dossierStatuses) || {}
                ).id;
                switch (code) {
                    case DossierStatusEnum.CREATION:
                        dossiersCount.completeness = allDossiers.filter(
                            (dossier: any) => {
                                return dossier.dossierStatus === idStatus;
                            }
                        ).length;
                        setDossiersCount({
                            state: dossiersCount,
                            type: "completeness",
                        });
                        break;
                    case DossierStatusEnum.EN_COURS_D_INSTRUCTION:
                        dossiersCount.instruction = allDossiers.filter(
                            (dossier: any) => {
                                return dossier.dossierStatus === idStatus;
                            }
                        ).length;
                        setDossiersCount({
                            state: dossiersCount,
                            type: "instruct",
                        });
                        break;
                    default:
                        dossiersCount.other += 1;
                        break;
                }
            });

            dossiersCount.all = allDossiers.length;
            setDossiersCount({ state: dossiersCount, type: "all" });
        }
    }, [dossierStatuses, dossiersCount]);

    const overViewWidgetItems: OverViewWidgetItem[] = useMemo(
        () => [
            {
                icon: (
                    <ReactSvgIcon
                        width="1.5rem"
                        height="1.5rem"
                        ReactIcon={DossierIcon}
                    />
                ),
                title: `${dossiersCount.all} dossier(s) au total`,
                onClick: () => {
                    updateContent(<Dossiers />);
                    setActive(ClientDashBoardNavBarMenuItemId.DOSSIERS);
                },
            },
            {
                icon: (
                    <ReactSvgIcon
                        width="1.5rem"
                        height="1.5rem"
                        ReactIcon={DossierEdit}
                    />
                ),
                title: `${dossiersCount.completeness} dossier(s) en cours de complétude`,
                onClick: () => {
                    updateContent(
                        <Dossiers
                            dossierStatusFilter={{
                                code: DossierStatusEnum.CREATION,
                            }}
                        />
                    );
                    setActive(ClientDashBoardNavBarMenuItemId.DOSSIERS);
                },
            },
            {
                icon: (
                    <ReactSvgIcon
                        width="1.5rem"
                        height="1.5rem"
                        ReactIcon={DossierEdit}
                    />
                ),
                title: `${dossiersCount.instruction} dossier(s) en cours d'instruction`,
                onClick: () => {
                    updateContent(
                        <Dossiers
                            dossierStatusFilter={{
                                code: DossierStatusEnum.EN_COURS_D_INSTRUCTION,
                            }}
                        />
                    );
                    setActive(ClientDashBoardNavBarMenuItemId.DOSSIERS);
                },
            },
        ],
        [Object.values(dossiersCount), setActive, updateContent]
    );
    const fetchCurrentDossiers = useCallback(async () => {
        await _fetchCurrentDossiers();
    }, [_fetchCurrentDossiers]);

    const currentDossiersSorted = useMemo(() => {
        const currentDossiersCloned = cloneObject(currentDossiers);
        const currentDossiersClonedFiltred = currentDossiersCloned.filter(
            (d: any) => d.submittedAt !== null
        );

        if (!currentDossiersClonedFiltred.length) {
            return currentDossiersCloned;
        }
        if (isDescendingSort) {
            return sortDescending(
                currentDossiersClonedFiltred,
                (d: DossierDetailType) => d.submittedAt
            );
        } else {
            return sortAscending(
                currentDossiersClonedFiltred,
                (d: DossierDetailType) => d.submittedAt
            );
        }
    }, [currentDossiers, isDescendingSort]);

    useEffect(() => {
        fetchCurrentDossiers();
    }, [fetchCurrentDossiers]);

    useEffect(() => {
        dossierCounter();
    }, [dossierCounter]);

    useEffect(() => {
        fetchRegionList();
    }, [fetchRegionList]);

    return (
        <>
            <header className="dossier-header">
                <Box
                    sx={{
                        marginLeft: "6rem",
                        marginTop: "5vh",
                        marginRight: "1.5rem",
                        marginBottom: "2rem",
                        display: "flex",
                        flexDirection: "column",
                        gap: "2.28rem",
                        "@media (max-width:1024px)": {
                            marginLeft: "1.5rem",
                        },
                    }}
                >
                    <HomeHeader
                        profilName={
                            currentUser.firstName + " " + currentUser.lastName
                        }
                        title={`Bienvenue sur votre espace administrateur région ${regionName}`}
                    />
                </Box>
            </header>
            <main className="layout-body" style={{ overflow: "hidden" }}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: "3rem",
                        marginLeft: "6rem",
                        marginRight: "6rem",
                        marginTop: "2rem",
                        width: "100%",
                        "@media (max-width:1024px)": {
                            marginRight: "1.5rem",
                            marginLeft: "1.5rem",
                            width: "100vh",
                            marginBottom: "5rem",
                        },
                        "@media (min-width: 600px) and (max-width:1024px)": {
                            width: "100vw",
                        },
                    }}
                >
                    <Grid container spacing={1.5}>
                        <Grid
                            item
                            xs={12}
                            sm={6}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: "1.5rem",
                                width: "100%",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-end",
                                    alignSelf: "stretch",
                                    gap: "0.5rem",
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: "Roboto",
                                        fontSize: "1.5rem",
                                        fontStyle: "normal",
                                        fontWeight: 500,
                                        lineHeight: "normal",
                                        color: "#023",
                                    }}
                                >
                                    Dossiers
                                </Typography>
                                <CustomSwapVert
                                    customheight="40px"
                                    onClick={() => {
                                        setisDescendingSort(!isDescendingSort);
                                    }}
                                    rotate={isDescendingSort}
                                />
                            </Box>

                            {currentDossiers.length
                                ? currentDossiersSorted.map((cd: any) => {
                                      return (
                                          <OverViewDossierWrapper
                                              key={cd.id}
                                              dossierDetail={cd}
                                              instructorList={instructorList}
                                              toBulkAssignedDossier={
                                                  toBulkAssignedDossier
                                              }
                                              downloadCertificate={
                                                  downloadCertificate
                                              }
                                              regionList={regionList}
                                          />
                                      );
                                  })
                                : null}
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            sm={6}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: "1.5rem",
                                width: "100%",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-end",
                                    alignSelf: "stretch",
                                    gap: "0.5rem",
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: "Roboto",
                                        fontSize: "1.5rem",
                                        fontStyle: "normal",
                                        fontWeight: 500,
                                        lineHeight: "normal",
                                        color: "#023",
                                    }}
                                >
                                    Vue d’ensemble
                                </Typography>
                                <ExportCsvBtn
                                    customheight="40"
                                    sx={{
                                        padding: 0,
                                    }}
                                    onClick={async () => {
                                        await exportCsv(
                                            userRoleId,
                                            null,
                                            region
                                        );
                                    }}
                                />
                            </Box>

                            {overViewWidgetItems ? (
                                <OverViewWidgets
                                    overViewWidgetItems={overViewWidgetItems}
                                />
                            ) : null}
                        </Grid>
                    </Grid>
                </Box>
            </main>
        </>
    );
}
